// 任务（首页，原「任务队列」+「全部任务」合成一个列表，2026-10-30 业主定）：
// 顶部 4 张统计卡（点卡片 = 只看这个状态）+「需要优先处理」；下面是全部任务（完成、失败的都在，删除 = 归档的不显示），
// 关键字 + 类型 / 状态 / 创建人 / 优先级多选 + 创建日期范围（同字段 OR、不同字段 AND）。
// 筛选、排序、分页全部写在 URL（返回键能回到原来的筛选；统计页的排行也靠它跳过来）；后端分页 /api/tasks/search；
// /ws 收到 task 事件就（防抖）刷新列表和统计
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { STATUS_META, type Priority, type TaskStatus } from "@csp/shared";
import { api } from "../lib/api";
import { PRIORITY_TEXT, REMOVE_HINT } from "../lib/format";
import { isStaff, useAuth } from "../lib/auth";
import { useDialog } from "../components/Dialogs";
import type { Column } from "../components/DataTable";
import type { TaskDTO } from "@csp/shared";
import { useAction, useInterval, useLoad } from "../lib/hooks";
import { fmtDateTime, fmtTime } from "../lib/format";
import { ATTENTION_STATUSES, countByStatus, sortQueue } from "../lib/queue";
import { useWsMessages } from "../lib/realtime";
import { StatusBadge } from "../components/Badges";
import { statusLabel } from "../lib/statusTone";
import {
  EMPTY_FILTERS,
  PAGE_SIZES,
  activeFilterCount,
  filtersToParams,
  parseFilters,
  totalPages,
  type SearchFilters,
  type SortKey,
} from "../lib/taskQuery";
import { PageHeading } from "../components/PageHeading";
import { ColumnPicker, DataTable, useHiddenColumns } from "../components/DataTable";
import { MultiSelectFilter, type Option } from "../components/MultiSelectFilter";
import { TASK_COLUMNS } from "../components/TaskColumns";

const STATUS_OPTIONS: Option[] = (Object.keys(STATUS_META) as TaskStatus[]).map((s) => ({ value: s, label: statusLabel(s) }));
const PRIORITY_OPTIONS: Option[] = (["urgent", "high", "normal", "low"] as Priority[]).map((p) => ({ value: p, label: PRIORITY_TEXT[p] }));

/** 统计卡：数字按状态上色，点卡片 = 只看这个状态 */
const STATS: { status: TaskStatus; label: string; tone: string }[] = [
  { status: "waiting_scan", label: "等扫码", tone: "danger" },
  { status: "review_required", label: "等审核", tone: "orange" },
  { status: "blocked", label: "卡住", tone: "danger" },
  { status: "running", label: "执行中", tone: "amber" },
];

/** 「需要优先处理」里的一句话原因 */
function attentionText(t: TaskDTO): string {
  if (t.status === "blocked") return t.blockReason || "AI 卡住了，需要你看一下";
  if (t.status === "waiting_scan") return "需要用手机 Singpass App 扫码登录";
  return "检查点等你核对后才能提交";
}

/** 筛选条件读写 URL；改筛选回到第 1 页 */
function useUrlFilters(): [SearchFilters, (patch: Partial<SearchFilters>) => void, () => void] {
  const [params, setParams] = useSearchParams();
  const filters = useMemo(() => parseFilters(params), [params]);
  const update = (patch: Partial<SearchFilters>) => {
    const next = { ...filters, ...patch };
    if (!("page" in patch)) next.page = 1;
    setParams(filtersToParams(next), { replace: true });
  };
  const clear = () => setParams(filtersToParams({ ...EMPTY_FILTERS, sort: filters.sort, pageSize: filters.pageSize }), { replace: true });
  return [filters, update, clear];
}

/** test = 管理员区「测试任务」：只看探索 / dry run / 批量测试的任务（普通「任务」列表不显示它们） */
export function TasksPage({ test = false }: { test?: boolean }) {
  const navigate = useNavigate();
  const [f, update, clear] = useUrlFilters();
  const [kw, setKw] = useState(f.q);
  const [hidden, toggleCol] = useHiddenColumns("csp-hidden-columns-all-tasks");
  const types = useLoad(() => api.types(true), []);
  const creators = useLoad(() => api.taskCreators(), []);

  // URL 里的关键字变了（清空 / 从统计页跳来 / 返回键）→ 同步到输入框
  useEffect(() => setKw(f.q), [f.q]);

  const badRange = !!(f.from && f.to && f.from > f.to);
  const apiParams = filtersToParams(f, true);
  if (test) apiParams.set("test", "only");
  const apiQuery = apiParams.toString();
  const list = useLoad(async () => (badRange ? null : api.searchTasks(new URLSearchParams(apiQuery))), [apiQuery, badRange]);
  // 统计卡和「需要优先处理」：只看正在进行的任务
  const active = useLoad(() => api.tasks({ status: [...ATTENTION_STATUSES, "running"].join(","), test: test ? "only" : undefined }), [test]);
  const counts = useMemo(() => countByStatus(active.data ?? []), [active.data]);
  const urgent = useMemo(() => sortQueue((active.data ?? []).filter((t) => ATTENTION_STATUSES.includes(t.status))), [active.data]);

  // 实时：任务有变化就刷新（合并 800ms 内的重复事件）；兜底每 30 秒刷一次
  const timer = useRef<number | null>(null);
  useWsMessages((m) => {
    if (m.type !== "task") return;
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      void list.reload();
      void active.reload();
    }, 800);
  });
  useInterval(() => {
    void list.reload();
    void active.reload();
  }, (active.data ?? []).length ? 30_000 : null);
  const pickStat = (s: TaskStatus) => update({ status: f.status.length === 1 && f.status[0] === s ? [] : [s] });

  const typeOptions: Option[] = (types.data ?? []).map((t) => ({ value: t.id, label: t.name }));
  // 从统计页带过来的类型可能已停用 / 不在列表里：补一项，筛选摘要不至于显示「全部」
  for (const id of f.typeId) if (!typeOptions.some((o) => o.value === id)) typeOptions.push({ value: id, label: "（其他类型）" });
  const creatorOptions: Option[] = (creators.data ?? []).map((u) => ({ value: u.id, label: u.displayName }));

  const { user } = useAuth();
  const dialog = useDialog();
  const act = useAction();
  const remove = async (t: TaskDTO) => {
    if (!(await dialog.confirm({ title: `删除任务 #${t.no}？`, body: <p>{REMOVE_HINT}</p>, okText: "删除", danger: true }))) return;
    if ((await act.run(`remove-${t.id}`, () => api.removeTask(t.id))) !== undefined) void list.reload();
  };
  // 操作列只给文员 / 管理员；已归档的不再显示删除
  const columns: Column<TaskDTO>[] = isStaff(user?.role)
    ? [
        ...TASK_COLUMNS,
        {
          key: "actions",
          label: "操作",
          fixed: true,
          render: (t) =>
            t.status === "archived" ? null : (
              <button
                type="button"
                className="btn btn-small btn-danger"
                disabled={!!act.busy}
                data-testid="row-remove"
                onClick={(e) => {
                  e.stopPropagation();
                  void remove(t);
                }}
              >
                删除
              </button>
            ),
        },
      ]
    : TASK_COLUMNS;

  const total = list.data?.total ?? 0;
  const pages = totalPages(total, f.pageSize);
  const used = activeFilterCount(f);

  return (
    <div>
      {test ? (
        <PageHeading title="测试任务" desc="探索（演练 / 正式）、dry run 和批量测试建的任务都在这里，文员的「任务」列表不显示它们；探索在任务类型详情里开始">
          <Link to="/admin/types" className="btn">
            任务类型
          </Link>
        </PageHeading>
      ) : (
        <PageHeading title="任务" desc="全部任务：进行中、已完成、失败的都在这里；删除的任务只在状态选了「已归档」时出现；测试任务在管理员区「测试任务」">
          <Link to="/stats" className="btn">
            任务统计
          </Link>
          <Link to="/tasks/new" className="btn btn-primary">
            ＋ 新建任务
          </Link>
        </PageHeading>
      )}

      <div className="stat-cards" data-testid="stat-cards">
        {STATS.map((c) => {
          const n = counts[c.status] ?? 0;
          const on = f.status.length === 1 && f.status[0] === c.status;
          return (
            <button key={c.status} type="button" className={`stat-card ${n ? c.tone : "zero"}${on ? " active" : ""}`} onClick={() => pickStat(c.status)} aria-pressed={on} data-stat={c.status}>
              <span className="num">{n}</span>
              <span className="lbl">{c.label}</span>
            </button>
          );
        })}
      </div>

      {urgent.length > 0 && (
        <div className="card" data-testid="urgent-list">
          <h2>
            需要优先处理 <span className="tab-count">{urgent.length}</span>
          </h2>
          {urgent.map((t) => (
            <Link key={t.id} to={`/tasks/${t.id}`} className="urgent" data-testid="urgent-item" data-urgent-no={t.no}>
              <StatusBadge status={t.status} />
              <span className="what">
                <b>
                  #{t.no} <span translate="no">{t.title}</span>
                </b>
                <span className="muted small"> · {t.clientEntity.uen ? `UEN ${t.clientEntity.uen}` : t.typeName}</span>
                <span className="small reason">{attentionText(t)}</span>
              </span>
              <span className="muted small" title={fmtDateTime(t.statusSince)}>
                {fmtTime(t.statusSince)}
              </span>
              <span className="btn btn-small">去处理 →</span>
            </Link>
          ))}
        </div>
      )}

      <div className="card">
        <div className="filter-row" data-testid="all-filters">
          <form
            className="row"
            onSubmit={(e) => {
              e.preventDefault();
              update({ q: kw.trim() });
            }}
          >
            <input type="search" placeholder="搜公司名 / UEN / 标题" value={kw} onChange={(e) => setKw(e.target.value)} aria-label="关键字" />
            <button className="btn btn-primary">查询</button>
          </form>
          <MultiSelectFilter label="类型" options={typeOptions} value={f.typeId} onChange={(v) => update({ typeId: v })} testId="type-filter" />
          <MultiSelectFilter label="状态" options={STATUS_OPTIONS} value={f.status} onChange={(v) => update({ status: v as TaskStatus[] })} testId="status-filter" />
          <MultiSelectFilter label="创建人" options={creatorOptions} value={f.createdBy} onChange={(v) => update({ createdBy: v })} testId="creator-filter" />
          <MultiSelectFilter label="优先级" options={PRIORITY_OPTIONS} value={f.priority} onChange={(v) => update({ priority: v as Priority[] })} testId="priority-filter" />
          <label className="row date-range">
            <span className="muted small">创建日期</span>
            <input type="date" value={f.from} max={f.to || undefined} onChange={(e) => update({ from: e.target.value })} aria-label="创建日期从" />
            <span className="muted">–</span>
            <input type="date" value={f.to} min={f.from || undefined} onChange={(e) => update({ to: e.target.value })} aria-label="创建日期到" />
          </label>
          {used > 0 && (
            <button type="button" className="btn btn-link small" onClick={clear} data-testid="clear-filters">
              清空筛选（已使用 {used} 项）
            </button>
          )}
        </div>
        {badRange && <div className="notice err">开始日期不能晚于结束日期</div>}

        <div className="list-bar">
          <span className="count" data-testid="all-count">
            共 {total} 个 · 第 {Math.min(f.page, pages)}/{pages} 页 · 日期按新加坡时间
          </span>
          <div className="row">
            <div className="seg" role="group" aria-label="排序">
              {(
                [
                  ["updatedAt", "最近更新"],
                  ["createdAt", "最近创建"],
                ] as [SortKey, string][]
              ).map(([k, label]) => (
                <button key={k} type="button" className={f.sort === k ? "active" : undefined} aria-pressed={f.sort === k} onClick={() => update({ sort: k })}>
                  {label}
                </button>
              ))}
            </div>
            <ColumnPicker columns={TASK_COLUMNS} hidden={hidden} onToggle={toggleCol} />
          </div>
        </div>

        {list.error && <div className="notice err">{list.error}</div>}
        {act.error && <div className="notice err">删除失败：{act.error}</div>}
        {list.loading && !list.data ? (
          <div className="empty">正在加载任务…</div>
        ) : (
          list.data && (
            <DataTable
              columns={columns}
              rows={list.data.items}
              rowKey={(t) => t.id}
              hidden={hidden}
              onRowClick={(t) => navigate(`/tasks/${t.id}`)}
              rowAttrs={(t) => ({ "data-testid": "task-row", "data-task-no": t.no, "data-status": t.status, "data-type-id": t.typeId })}
              empty={used ? "没有符合筛选条件的任务" : "还没有任务，点右上角「＋ 新建任务」开始"}
            />
          )
        )}

        <div className="pager" data-testid="pager">
          <label className="row">
            <span className="muted small">每页</span>
            <select value={f.pageSize} onChange={(e) => update({ pageSize: Number(e.target.value) })} aria-label="每页条数">
              {PAGE_SIZES.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
          <span className="spacer" />
          <button type="button" className="btn btn-small" disabled={f.page <= 1} onClick={() => update({ page: f.page - 1 })}>
            上一页
          </button>
          <span className="small">
            第 {Math.min(f.page, pages)} / {pages} 页
          </span>
          <button type="button" className="btn btn-small" disabled={f.page >= pages} onClick={() => update({ page: f.page + 1 })}>
            下一页
          </button>
        </div>
      </div>
    </div>
  );
}
